import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { TextField } from '@mui/material';
import { composeEvent, composeEventAsync } from '@icp/utils';
import { useForkRef } from '@icp/hooks';
import InputClearButton from './InputClearButton';

const NoI18nInput = forwardRef(function NoI18nInput(props, ref) {
  const {
    value: valueProp,
    changeDelayToBlur,
    onChange,
    onBlur,
    onKeyDown,
    onKeyUp,
    readonly,
    allowClear,
    inputMode, // mui 直接在 componentProps 里戏鹅的其余属性传不到 html 的 input 里，这里额外支持一下。
    inputRef: inputRefProp,
    onClear,
    ...other
    //
  } = props;

  const inputRef = useRef();
  const handleInputRef = useForkRef(inputRef, inputRefProp);
  const [value, setValue] = useState(valueProp);
  // 让 keydown 和 keyup 之间 onChange 只会触发一次，防止一致按住 Enter 键反复触发 onChange
  const once = useRef(false);

  useEffect(() => {
    setValue(valueProp);
  }, [valueProp]);

  const handleChange = (event) => {
    setValue(event.target.value);
    if (!changeDelayToBlur) {
      onChange(event.target.value);
    } else if (document.activeElement !== inputRef.current) {
      // 当前 focus 元素不是 input 同时他还触发了 onchange, 表示可能是浏览器 autocomplete 或者其余的非用户主动输入的情况
      onChange(event.target.value);
    }
  };

  const handleBlur = (event) => {
    if (changeDelayToBlur) {
      return onChange(event.target.value);
    }
    return null;
  };

  // 当 changeDelayToBlur 时，按回车键也需要触发 onChange。和原生 input 逻辑一致
  const handleKeyDown = (event) => {
    if (!changeDelayToBlur) {
      return;
    }

    if (once.current) {
      return;
    }

    if (event.key === 'Enter') {
      once.current = true;
      onChange(value);
    }
  };

  const handleKeyUp = (event) => {
    if (!changeDelayToBlur) {
      return;
    }

    if (event.key === 'Enter') {
      once.current = false;
    }
  };

  return (
    <TextField
      {...other}
      value={value ?? ''}
      onChange={handleChange}
      onBlur={composeEventAsync(handleBlur, onBlur)}
      onKeyDown={composeEvent(handleKeyDown, onKeyDown)}
      onKeyUp={composeEvent(handleKeyUp, onKeyUp)}
      InputProps={{
        readOnly: readonly,
        endAdornment:
          !readonly && value && allowClear ? <InputClearButton onClear={onClear} /> : null,
        inputProps: {
          inputMode,
        },
      }}
      inputRef={handleInputRef}
      ref={ref}
    />
  );
});

NoI18nInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  changeDelayToBlur: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  readonly: PropTypes.bool,
  allowClear: PropTypes.bool,
  inputMode: PropTypes.string,
  inputRef: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func]),
  onClear: PropTypes.func,
};

export default NoI18nInput;
